// src/pages/Menu/Articulacion/Admin/StatutesReportsCard.jsx
// Admin — Estatutos / Documentos (ajuste CTA responsive)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  Trash2,
  Edit2,
  Save,
  X,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Loader2,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import * as Svc from "../../../services/statutesService";
import { useNavigate } from "react-router-dom";
import { Banner } from "../../common/Banner";
import TooltipHelper from "../../common/TooltipHelper";
import SectionTips from "../../common/SectionTips";

const ORDER_FIELD = "position";

const input =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50`;
const btnWarn = `${btn} border bg-white hover:bg-amber-50 text-amber-800 border-amber-300`;

const yearsOptions = (() => {
  const y = new Date().getFullYear();
  const arr = [];
  for (let i = y + 1; i >= 2000; i--) arr.push(i);
  arr.push("Sin año");
  return arr;
})();

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const DirtyBadge = ({ show, className = "" }) =>
  show ? (
    <span
      className={`ml-2 inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 ${className}`}
    >
      Modificado
    </span>
  ) : null;

const ChipTitle = ({ children }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
    <span className="inline-block w-1.5 h-5 bg-[#0488AF] rounded-full" />
    {children}
  </div>
);

const Divider = () => (
  <div className="my-4">
    <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full" />
  </div>
);


/** Switch reutilizable (docs, principios, CTA) */
const Switch = ({
  checked,
  onChange,
  titleOn = "Visible para socios",
  titleOff = "Oculto para socios",
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-[#0488AF]" : "bg-gray-300"
    }`}
    title={checked ? titleOn : titleOff}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

function YearModal({
  open,
  year,
  items = [],
  onClose,
  onBeginEdit,
  onRemove,
  onMove,
  onToggleVisible,
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="relative w-[min(960px,92vw)] max-h-[82vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="p-5 border-b bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight">
                    Documentos {String(year)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {items.length} documento{items.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/70"
                >
                  <X />
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              {items.map((r, idx) => {
                const canUp = idx > 0;
                const canDown = idx < items.length - 1;
                return (
                  <div
                    key={r.id}
                    className="group border rounded-xl p-4 flex flex-col md:flex-row md:items-start justify-between gap-4 hover:shadow-sm min-w-0"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <FileText className="w-5 h-5 text-[#0488AF]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="font-medium truncate break-words"
                            title={
                              r.title?.trim() || `Documento ${r.year ?? ""}`
                            }
                          >
                            {r.title?.trim() || `Documento ${r.year ?? ""}`}
                          </span>
                          {r.version && (
                            <span className="text-xs border rounded px-1.5 py-0.5 bg-gray-50 text-gray-700">
                              v{r.version}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          Posición:{" "}
                          {typeof r?.[ORDER_FIELD] === "number" &&
                          r[ORDER_FIELD] > 0
                            ? r[ORDER_FIELD]
                            : idx + 1}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto mt-3 md:mt-0 md:justify-end flex-shrink-0">
                      <Switch
                        checked={!!r.visibleForMembers}
                        onChange={(v) => onToggleVisible(r.id, v)}
                      />
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                        onClick={() => onMove(year, r.id, -1)}
                        disabled={!canUp}
                        title="Mover arriba"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                        onClick={() => onMove(year, r.id, +1)}
                        disabled={!canDown}
                        title="Mover abajo"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                        onClick={() => onBeginEdit(r)}
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50"
                        onClick={() => onRemove(r.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function StatutesReportsCard({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  // Subir
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("");
  const [year, setYear] = useState("");
  const [visibleNew, setVisibleNew] = useState(true);
  const fileRef = useRef(null);

  // Progreso
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const progressTimerRef = useRef(null);

  // Edición
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editVersion, setEditVersion] = useState("");
  const [editYear, setEditYear] = useState(new Date().getFullYear());
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingEditInline, setSavingEditInline] = useState(false);

  // Config
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [principlesVisible, setPrinciplesVisible] = useState(true);
  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [lastVisibleAt, setLastVisibleAt] = useState(null);

  const [origCfg, setOrigCfg] = useState(null);

  // Reordenamiento…
  const [dirtyYears, setDirtyYears] = useState(new Set());
  const [snapshotByYear, setSnapshotByYear] = useState({});
  const [savingYearOrder, setSavingYearOrder] = useState(null);
  const [openYearModal, setOpenYearModal] = useState(null);
  const [dirtyYearsState, setDirtyYearsState] = useState({});

  const showLoading = (title = "Procesando…") =>
    Swal.fire({
      title,
      html: "Por favor espera un momento.",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
  const askConfirm = (opts = {}) =>
    Swal.fire({
      icon: "question",
      showCancelButton: true,
      confirmButtonText: opts.confirmButtonText || "Sí, continuar",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
      ...opts,
    });
  const toast = (title = "Listo", icon = "success", ms = 900) =>
    Swal.fire({ icon, title, timer: ms, showConfirmButton: false });

  function startFakeProgress() {
    stopFakeProgress();
    setUploadPct(10);
    progressTimerRef.current = setInterval(() => {
      setUploadPct((p) =>
        p >= 95 ? 95 : p + Math.max(1, Math.round((100 - p) * 0.05))
      );
    }, 180);
  }
  function stopFakeProgress() {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }

  async function load(yearFilter) {
    try {
      setLoading(true);
      const data = await Svc.listAdminStatutes(yearFilter);
      const items = Array.isArray(data?.items) ? data.items : [];
      setList(items);

      setCfgLoading(true);
      const cfg = data?.config || (await Svc.getStatutesConfig());
      const current = {
        pageTitle: cfg?.pageTitle || "",
        subtitle: cfg?.subtitle || "",
        principlesVisible: !!cfg?.principlesVisible,
        ctaEnabled: !!cfg?.ctaEnabled,
        ctaLabel: cfg?.ctaLabel || "",
        ctaUrl: cfg?.ctaUrl || "",
        lastVisibleAt: cfg?.lastVisibleAt || null,
      };
      setPageTitle(current.pageTitle);
      setSubtitle(current.subtitle);
      setPrinciplesVisible(current.principlesVisible);
      setCtaEnabled(current.ctaEnabled);
      setCtaLabel(current.ctaLabel);
      setCtaUrl(current.ctaUrl);
      setLastVisibleAt(current.lastVisibleAt);

      setOrigCfg({
        pageTitle: current.pageTitle,
        subtitle: current.subtitle,
        principlesVisible: current.principlesVisible,
        ctaEnabled: current.ctaEnabled,
        ctaLabel: current.ctaLabel,
        ctaUrl: current.ctaUrl,
      });
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudieron cargar los documentos",
        "error"
      );
    } finally {
      setLoading(false);
      setCfgLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const yearsInList = useMemo(() => {
    const set = new Set();
    for (const r of list) {
      if (r.year == null) {
        set.add("Sin año");
        continue;
      }
      set.add(Number(r.year));
    }
    return Array.from(set).sort((a, b) => {
      if (a === "Sin año") return 1;
      if (b === "Sin año") return -1;
      return b - a;
    });
  }, [list]);

  const safePos = (r) => {
    const p = Number(r?.[ORDER_FIELD]);
    return Number.isInteger(p) && p > 0 ? p : Number.POSITIVE_INFINITY;
  };

  const groupsByYear = useMemo(() => {
    const map = new Map();
    for (const r of list) {
      const key = r.year == null ? "Sin año" : Number(r.year);
      const arr = map.get(key) || [];
      arr.push(r);
      map.set(key, arr);
    }
    const sortInYear = (arr) =>
      [...arr].sort((a, b) => {
        const pa = safePos(a),
          pb = safePos(b);
        if (pa !== pb) return pa - pb;
        const ca = a?.createdAt ? +new Date(a.createdAt) : 0;
        const cb = b?.createdAt ? +new Date(b.createdAt) : 0;
        if (ca !== cb) return ca - cb;
        return (a.title || "")
          .toLowerCase()
          .localeCompare((b.title || "").toLowerCase());
      });
    for (const [y, arr] of map) map.set(y, sortInYear(arr));
    return map;
  }, [list]);

  const yearsSorted = useMemo(
    () =>
      Array.from(groupsByYear.keys()).sort((a, b) => {
        if (a === "Sin año") return 1;
        if (b === "Sin año") return -1;
        return b - a;
      }),
    [groupsByYear]
  );

  const setListForYear = (y, newArr) => {
    setList((prev) => {
      const others = prev.filter((r) => {
        const key = r.year == null ? "Sin año" : Number(r.year);
        return key !== y;
      });
      return [...others, ...newArr];
    });
  };

  const moveInYear = (y, id, direction) => {
    const currentArr = groupsByYear.get(y) || [];
    const idx = currentArr.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= currentArr.length) return;

    if (!(y in dirtyYearsState))
      setSnapshotByYear((s) => ({
        ...s,
        [y]: currentArr.map((r) => ({ ...r })),
      }));

    const arr = [...currentArr];
    const [item] = arr.splice(idx, 1);
    arr.splice(newIdx, 0, item);
    const arrWithPos = arr.map((r, i) => ({ ...r, [ORDER_FIELD]: i + 1 }));
    setListForYear(y, arrWithPos);
    setDirtyYears((prev) => new Set([...prev, y]));
    setDirtyYearsState((s) => ({ ...s, [y]: true }));
  };

  const saveYearOrder = async (y) => {
    if (y === "Sin año")
      return Swal.fire(
        "Orden",
        "No se puede reordenar el grupo 'Sin año'. Asigna un año primero.",
        "info"
      );
    const arr = groupsByYear.get(y) || [];
    if (!arr.length) return;
    const ok = await askConfirm({
      title: `¿Guardar nuevo orden del ${y}?`,
      text: "Se actualizarán las posiciones.",
      confirmButtonText: "Sí, guardar",
    });
    if (!ok.isConfirmed) return;
    try {
      setSavingYearOrder(y);
      showLoading("Guardando orden…");
      const ids = arr.map((r) => r.id);
      await Svc.reorderByYear(Number(y), ids);
      Swal.close();
      await toast("Orden guardado");
      setDirtyYears((prev) => {
        const next = new Set(prev);
        next.delete(y);
        return next;
      });
      setSnapshotByYear((s) => {
        const { [y]: _, ...rest } = s;
        return rest;
      });
      setDirtyYearsState((s) => {
        const { [y]: _, ...rest } = s;
        return rest;
      });
      await load();
    } catch (e) {
      Swal.close();
      console.error(e);
      Swal.fire(
        "No se guardó el orden",
        e?.message || "No se pudo guardar el nuevo orden.",
        "info"
      );
    } finally {
      setSavingYearOrder(null);
    }
  };

  const discardYearOrder = (y) => {
    const snap = snapshotByYear[y];
    if (!snap) return;
    setListForYear(
      y,
      snap.map((r) => ({ ...r }))
    );
    setDirtyYears((prev) => {
      const next = new Set(prev);
      next.delete(y);
      return next;
    });
    setSnapshotByYear((s) => {
      const { [y]: _, ...rest } = s;
      return rest;
    });
    setDirtyYearsState((s) => {
      const { [y]: _, ...rest } = s;
      return rest;
    });
  };

  const hasVisibleDocs = useMemo(
    () => list.some((r) => !!r.visibleForMembers),
    [list]
  );

  const isDirty = (key, curr, orig = origCfg) => {
    if (!orig) return false;
    const a = (curr ?? "").toString().trim();
    const b = (orig[key] ?? "").toString().trim();
    return a !== b;
  };

  const restoreField = (key) => {
    if (!origCfg) return;
    const val = origCfg[key];
    switch (key) {
      case "pageTitle":
        setPageTitle(val || "");
        break;
      case "subtitle":
        setSubtitle(val || "");
        break;
      case "principlesVisible":
        setPrinciplesVisible(!!val);
        break;
      case "ctaEnabled":
        setCtaEnabled(!!val);
        break;
      case "ctaLabel":
        setCtaLabel(val || "");
        break;
      case "ctaUrl":
        setCtaUrl(val || "");
        break;
      default:
        break;
    }
  };

  const restoreAllConfig = () => {
    if (!origCfg) return;
    setPageTitle(origCfg.pageTitle || "");
    setSubtitle(origCfg.subtitle || "");
    setPrinciplesVisible(!!origCfg.principlesVisible);
    setCtaEnabled(!!origCfg.ctaEnabled);
    setCtaLabel(origCfg.ctaLabel || "");
    setCtaUrl(origCfg.ctaUrl || "");
  };

  const saveTextsAndCta = async () => {
    if (ctaEnabled) {
      if (!ctaLabel.trim())
        return Swal.fire("CTA", "Debes indicar el nombre del botón.", "info");
      if (!Svc.isValidCtaUrl((ctaUrl || "").trim()))
        return Swal.fire(
          "CTA",
          "La URL debe iniciar con http(s):// o mailto:",
          "warning"
        );
    }
    try {
      setCfgSaving(true);
      showLoading("Guardando…");
      const saved = await Svc.updateStatutesConfig({
        pageTitle,
        subtitle,
        ctaEnabled,
        ctaLabel: ctaLabel.trim(),
        ctaUrl: ctaUrl.trim(),
        principlesVisible,
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      setOrigCfg({
        pageTitle: saved?.pageTitle || "",
        subtitle: saved?.subtitle || "",
        principlesVisible: !!saved?.principlesVisible,
        ctaEnabled: !!saved?.ctaEnabled,
        ctaLabel: saved?.ctaLabel || "",
        ctaUrl: saved?.ctaUrl || "",
      });
      Swal.close();
      await toast("Configuración guardada");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo actualizar la configuración",
        "error"
      );
    } finally {
      setCfgSaving(false);
    }
  };

  const saveAllEdits = async () => {
    if (ctaEnabled) {
      if (!ctaLabel.trim())
        return Swal.fire("CTA", "Debes indicar el nombre del botón.", "info");
      if (!Svc.isValidCtaUrl((ctaUrl || "").trim()))
        return Swal.fire(
          "CTA",
          "La URL debe iniciar con http(s):// o mailto:",
          "warning"
        );
    }
    try {
      setCfgSaving(true);
      showLoading("Guardando…");
      const savedCfg = await Svc.updateStatutesConfig({
        pageTitle,
        subtitle,
        ctaEnabled,
        ctaLabel: ctaLabel.trim(),
        ctaUrl: ctaUrl.trim(),
        principlesVisible,
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      setOrigCfg({
        pageTitle: savedCfg?.pageTitle || "",
        subtitle: savedCfg?.subtitle || "",
        principlesVisible: !!savedCfg?.principlesVisible,
        ctaEnabled: !!savedCfg?.ctaEnabled,
        ctaLabel: savedCfg?.ctaLabel || "",
        ctaUrl: savedCfg?.ctaUrl || "",
      });
      Swal.close();
      await toast("Cambios guardados");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar todo", "error");
    } finally {
      setCfgSaving(false);
    }
  };

  // Subida
  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      // Validar si es PDF para mostrar mensaje con link de compresión
      const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
      if (isPdf) {
        return Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          html: `El archivo "${f.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
          confirmButtonText: "Entendido",
        });
      }
      return Swal.fire("Archivo muy grande", `Máximo 15 MB.`, "warning");
    }
    setFile(f);
    if (!title.trim()) {
      const base = (f.name || "").replace(
        /\.(pdf|docx?|xls[xm]?|ppt[xm]?|txt|csv)$/i,
        ""
      );
      setTitle(base);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return Swal.fire("Atención", "Selecciona un archivo", "info");
    if (!title.trim())
      return Swal.fire("Atención", "Escribe el título del documento", "info");
    if (!year.trim())
      return Swal.fire("Atención", "Debes indicar el año del documento", "info");
    const yearValue = Number(year.trim());
    if (isNaN(yearValue) || yearValue < 1976 || yearValue > 2100)
      return Swal.fire("Atención", "El año debe ser un número válido entre 1976 y 2100", "info");
    try {
      setUploading(true);
      startFakeProgress();
      await Svc.uploadStatute({
        file,
        title: title.trim(),
        year: yearValue,
        version: (version || "").trim() || undefined,
        visibleForMembers: !!visibleNew,
      });
      setUploadPct(100);
      await toast("Documento subido");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTitle("");
      setVersion("");
      setYear("");
      setVisibleNew(true);
      await load();
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudo subir el documento",
        "error"
      );
    } finally {
      stopFakeProgress();
      setUploading(false);
      setTimeout(() => setUploadPct(0), 400);
    }
  };

  // Edición item
  const beginEdit = (r) => {
    setEditingId(r.id);
    setEditTitle(r.title || "");
    setEditVersion(r.version || "");
    setEditYear(r.year ?? new Date().getFullYear());
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditVersion("");
    setEditYear(new Date().getFullYear());
    setSavingEdit(false);
  };

  const saveEdit = async (id) => {
    try {
      setSavingEdit(true);
      showLoading("Guardando…");
      const payload = {
        title: (editTitle || "").trim() || undefined,
        year: editYear === "Sin año" ? null : Number(editYear),
        version: (editVersion || "").trim() || undefined,
      };
      await Svc.updateStatute(id, payload);
      Swal.close();
      await toast("Actualizado");
      cancelEdit();
      await load();
    } catch (e) {
      Swal.close();
      console.error(e);
      Swal.fire("Error", e?.message || "No se pudo actualizar", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleVisible = async (id, visible) => {
    try {
      setSavingEditInline(true);
      await Svc.updateStatute(id, { visibleForMembers: !!visible });
      await load();
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No se pudo cambiar visibilidad",
        "error"
      );
    } finally {
      setSavingEditInline(false);
    }
  };

  // Eliminar documento
  const handleRemove = async (id) => {
    const doc = list.find((r) => r.id === id);
    const res = await askConfirm({
      title: "Eliminar documento",
      text: `¿Seguro que deseas eliminar "${
        doc?.title || "este documento"
      }"? Esta acción no se puede deshacer.`,
      confirmButtonText: "Sí, eliminar",
    });
    if (!res.isConfirmed) return;

    try {
      showLoading("Eliminando…");
      await Svc.removeStatute(id);
      Swal.close();
      await toast("Documento eliminado");
      await load();
    } catch (e) {
      console.error(e);
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo eliminar el documento",
        "error"
      );
    }
  };

  return (
    <div className="w-full overflow-x-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="max-w-screen-xl mx-auto px-4 py-6 space-y-6"
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver al menú principal
        </button>

        <Banner
          title="Estatutos"
          message="Sube, edita, organiza y controla visibilidad de documentos."
        />

        <SectionTips
          section="la sección de Estatutos"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar Estatutos?"
          tipsList={[
            'En esta sección puedes subir, editar y organizar los estatutos de la organización por año.',
            'Puedes controlar la visibilidad de cada documento individualmente mediante el switch "Visible para socios".',
            'Los documentos se organizan por año, y puedes reordenar dentro de cada año usando los botones ↑/↓.',
            'Puedes editar el título, versión y año de cada documento después de subirlo.',
            'En la configuración puedes personalizar los textos visibles en la página pública, como el título de página y subtítulo.',
          ]}
          finalNote="Consejo: Mantén los documentos organizados por año para facilitar el acceso de los socios."
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766415914/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_Estatutos_m3gkvw.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:items-start min-w-0">
          {/* Subir */}
          <div
            className="rounded-2xl p-[1.5px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5">
                <h2 className="text-lg font-semibold mb-3">
                  Subir documento
                </h2>

                <form
                  onSubmit={handleUpload}
                  className="grid gap-3 md:grid-cols-3 items-end"
                >
                  <div className="md:col-span-1 flex items-end gap-2">
                    <label className={`${btnGhost} cursor-pointer`}>
                      <Upload size={16} />
                      Seleccionar archivo
                      <input
                        ref={fileRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  <div className="md:col-span-3 flex items-center">
                    <TooltipHelper text="Selecciona un archivo PDF de máximo 15 MB. El archivo se subirá y estará disponible para visualización y descarga." />
                  </div>

                  {file && (
                    <div className="md:col-span-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl border bg-slate-50">
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                          <FileText className="w-5 h-5 text-[#0488AF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`${btnGhost} shrink-0`}
                          onClick={() => {
                            setFile(null);
                            if (fileRef.current) fileRef.current.value = "";
                          }}
                          disabled={uploading}
                          title="Quitar archivo"
                        >
                          <X size={16} /> Quitar
                        </button>
                      </div>
                    </div>
                  )}

                  <label className="md:col-span-3">
                    <span className="block text-sm text-gray-700 mb-1">
                      Título
                    </span>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Ej: Estatutos 2025"
                      className={input}
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1">
                      Versión (opcional)
                    </span>
                    <input
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      placeholder="1.0 / 2025-06"
                      className={input}
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1">
                      Año <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="text"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      placeholder={`Año (${new Date().getFullYear()})`}
                      className={input}
                      disabled={uploading}
                      required
                    />
                  </label>

                  <div className="md:col-span-3 flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={
                        !file || loading || uploading || !title.trim() || !year.trim()
                      }
                      className={btnPri}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />{" "}
                          Subiendo… {uploadPct ? `${uploadPct}%` : ""}
                        </>
                      ) : (
                        <>Subir documento</>
                      )}
                    </button>

                    {uploading && (
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[120px]">
                        <div
                          className="h-full bg-[#0488AF] transition-all"
                          style={{ width: `${uploadPct || 25}%` }}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Config / Textos / CTA */}
          <div
            className="rounded-2xl p-[2px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #DCEBFF 0%, #DDF8FF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Textos visibles en Estatutos
                  </h3>
                  <button
                    type="button"
                    onClick={restoreAllConfig}
                    disabled={!origCfg || (pageTitle === (origCfg.pageTitle || "") && subtitle === (origCfg.subtitle || "") && principlesVisible === !!origCfg.principlesVisible && ctaEnabled === !!origCfg.ctaEnabled && ctaLabel === (origCfg.ctaLabel || "") && ctaUrl === (origCfg.ctaUrl || ""))}
                    className={btnWarn}
                    title="Descartar todos los cambios y restaurar valores originales"
                  >
                    <X size={14} /> Descartar cambios
                  </button>
                </div>

                {/* ENCABEZADOS */}
                <div className="rounded-xl border bg-[#F8FAFC] p-4">
                  <ChipTitle>Encabezados en la página pública</ChipTitle>

                  <div className="grid gap-3 mt-3">
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm text-gray-700 flex items-center gap-1">
                          Título de página
                          <DirtyBadge
                            show={isDirty("pageTitle", pageTitle)}
                          />
                          <TooltipHelper text='Si lo dejas vacío, se usará "Estatutos y documentos".' />
                        </label>
                      </div>
                      <input
                        className={input}
                        value={pageTitle}
                        onChange={(e) => setPageTitle(e.target.value)}
                        placeholder="Estatutos y documentos"
                      />
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm text-gray-700 flex items-center gap-1">
                          Subtítulo
                          <DirtyBadge show={isDirty("subtitle", subtitle)} />
                          <TooltipHelper text='Si lo dejas vacío, se usará "Documentos oficiales de la organización".' />
                        </label>
                      </div>
                      <input
                        className={input}
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        placeholder="Documentos oficiales de la organización"
                      />
                    </div>
                  </div>
                </div>

                {/* PRINCIPIOS & CTA (solo botones de guardado por ahora) */}
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 pt-3 border-t">
                    <button
                      type="button"
                      onClick={saveTextsAndCta}
                      disabled={cfgLoading || cfgSaving}
                      className={btnPri}
                    >
                      <Save size={16} /> Guardar sección
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Listado por año */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Documentos subidos</h3>
        </div>
        {loading ? (
          <div className="text-gray-500 py-6">Cargando…</div>
        ) : yearsSorted.length === 0 ? (
          <div className="text-gray-500 py-6">Sin documentos</div>
        ) : (
          <div className="space-y-6">
            {yearsSorted.map((y, yi) => {
              const arr = groupsByYear.get(y) || [];
              const isDirtyY = dirtyYears.has(y);
              const isSaving = savingYearOrder === y;
              return (
                <motion.div
                  key={String(y)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: yi * 0.05 }}
                >
                  <div
                    className="rounded-2xl p-[1.5px]"
                    style={{
                      background:
                        "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
                    }}
                  >
                    <div className="rounded-2xl bg-white ring-1 ring-black/5">
                      <div className="p-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                          <div className="flex items-center gap-3">
                            <div className="px-3 py-1.5 rounded-2xl bg-[#0B4A8F] text-white font-semibold text-lg">
                              {String(y)}
                            </div>
                            <div className="text-sm text-gray-600">
                              {arr.length} documento
                              {arr.length === 1 ? "" : "s"}
                            </div>
                            <DirtyBadge show={isDirtyY} />
                          </div>
                          <div className="flex flex-wrap gap-2 items-center">
                            {isDirtyY ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => saveYearOrder(y)}
                                  disabled={isSaving}
                                  className={`${btnPri} px-3 py-1.5 rounded-lg`}
                                >
                                  <Save size={16} /> Guardar orden
                                </button>
                                <button
                                  type="button"
                                  onClick={() => discardYearOrder(y)}
                                  disabled={isSaving}
                                  className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                >
                                  <X size={16} /> Descartar
                                </button>
                              </>
                            ) : (
                              <div className="text-xs text-gray-500">
                                Usa ↑/↓ para ordenar y confirma con “Guardar
                                orden”.
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {arr.map((r, idx) => {
                            const isEditing = editingId === r.id;
                            const canMoveUp = idx > 0;
                            const canMoveDown = idx < arr.length - 1;
                            const disabledMove = isSaving;
                            return (
                              <motion.div
                                key={r.id}
                                layout
                                transition={{
                                  type: "spring",
                                  stiffness: 500,
                                  damping: 40,
                                }}
                                className="border rounded-xl p-3 flex flex-col md:flex-row md:items-start justify-between gap-3 bg-white/70 min-w-0"
                              >
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                                    <FileText className="w-5 h-5 text-[#0488AF]" />
                                  </div>

                                  {isEditing ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                      <input
                                        value={editTitle}
                                        onChange={(e) =>
                                          setEditTitle(e.target.value)
                                        }
                                        className={`${input} w-[16rem]`}
                                        placeholder="Título"
                                      />
                                      <input
                                        value={editVersion}
                                        onChange={(e) =>
                                          setEditVersion(e.target.value)
                                        }
                                        className={`${input} w-[7rem]`}
                                        placeholder="Versión"
                                      />
                                      <select
                                        value={editYear}
                                        onChange={(e) =>
                                          setEditYear(
                                            isNaN(Number(e.target.value))
                                              ? "Sin año"
                                              : Number(e.target.value)
                                          )
                                        }
                                        className={`${input} w-[8rem]`}
                                      >
                                        {yearsOptions.map((yy) => (
                                          <option key={yy} value={yy}>
                                            {yy}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  ) : (
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span
                                          className="font-medium truncate break-words"
                                          title={
                                            r.title?.trim() ||
                                            `Documento ${r.year ?? ""}`
                                          }
                                        >
                                          {r.title?.trim() ||
                                            `Documento ${r.year ?? ""}`}
                                        </span>
                                        {r.version && (
                                          <span className="text-xs border rounded px-1.5 py-0.5 bg-gray-50 text-gray-700">
                                            v{r.version}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600">
                                        Posición:{" "}
                                        {typeof r?.[ORDER_FIELD] ===
                                          "number" && r[ORDER_FIELD] > 0
                                          ? r[ORDER_FIELD]
                                          : idx + 1}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto mt-2 md:mt-0 md:justify-end flex-shrink-0">
                                  <div className="flex items-center gap-2 px-2 py-1 rounded border bg-white">
                                    <Switch
                                      checked={!!r.visibleForMembers}
                                      onChange={(v) => toggleVisible(r.id, v)}
                                    />
                                    <span className="text-xs">
                                      {r.visibleForMembers
                                        ? "Visible para socios"
                                        : "Oculto para socios"}
                                    </span>
                                  </div>
                                  {!isEditing && (
                                    <>
                                      <button
                                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                                        disabled={!canMoveUp || disabledMove}
                                        onClick={() =>
                                          moveInYear(y, r.id, -1)
                                        }
                                        title="Mover arriba"
                                      >
                                        <ChevronUp size={16} />
                                      </button>
                                      <button
                                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                                        disabled={!canMoveDown || disabledMove}
                                        onClick={() =>
                                          moveInYear(y, r.id, +1)
                                        }
                                        title="Mover abajo"
                                      >
                                        <ChevronDown size={16} />
                                      </button>
                                    </>
                                  )}
                                  {isEditing ? (
                                    <>
                                      <button
                                        className={`${btnGhost} px-3 py-1.5 rounded-lg disabled:opacity-60`}
                                        disabled={savingEdit}
                                        onClick={() => saveEdit(r.id)}
                                        title="Guardar"
                                      >
                                        <Save size={16} />
                                      </button>
                                      <button
                                        className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                        onClick={cancelEdit}
                                        title="Cancelar"
                                      >
                                        <X size={16} />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                      onClick={() => beginEdit(r)}
                                      title="Editar"
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                  )}
                                  <button
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50"
                                    onClick={() => handleRemove(r.id)}
                                    title="Eliminar"
                                  >
                                    <Trash2 size={16} /> Eliminar
                                  </button>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      <YearModal
        open={openYearModal != null}
        year={openYearModal ?? undefined}
        items={
          openYearModal != null ? groupsByYear.get(openYearModal) || [] : []
        }
        onClose={() => setOpenYearModal(null)}
        onBeginEdit={(r) => {
          setOpenYearModal(null);
          beginEdit(r);
        }}
        onRemove={handleRemove}
        onMove={(y, id, d) => moveInYear(y, id, d)}
        onToggleVisible={toggleVisible}
      />
    </div>
  );
}

